import { Button, Card } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as PauseIcon } from "../../Icons/vuesax/pause.svg";
import { ReactComponent as PlayIcon } from "../../Icons/vuesax/play.svg";
import { ReactComponent as VolumeCrossIcon } from "../../Icons/vuesax/volume-cross.svg";
import { ReactComponent as VolumeHighIcon } from "../../Icons/vuesax/volume-high.svg";
interface AudioPlayerProps {
  src?: string | File | Blob;
  className: string;
  loading?: boolean;
  style: React.CSSProperties;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, className, loading, style }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [audioSrc, setAudioSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    setIsPlaying(false);
    if (src instanceof Blob) {
      const url = URL.createObjectURL(src);
      setAudioSrc(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (typeof src === "string") {
      setAudioSrc(src);
    }
  }, [src]);

  const togglePlayPause = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        await audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error("Playback error:", error);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const newTime = (parseFloat(e.target.value) / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <Card
      styles={{ body: { padding: 0, width:'100%', ...style} }}
      className={`audio-player-card ${className}`}>
      <div className="audio-player-controls">
        <button onClick={togglePlayPause} className="play-pause-button" disabled={loading}>
          {isPlaying ? (
            <PauseIcon width="1.25rem" height="1.25rem" fill="#1868db" color="#1868db"/>
          ) : (
            <PlayIcon width="1.25rem" height="1.25rem" fill="#1868db" color="#1868db"/>
          )}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          value={duration ? (currentTime / duration) * 100 : 0}
          onChange={handleSeek}
          className="audio-slider"
          disabled={loading}
        />
        <span className="audio-time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <Button onClick={toggleMute} disabled={loading} type="text">
          {isMuted ? (
            <VolumeCrossIcon width="20px" height="20px" />
          ) : (
            <VolumeHighIcon width="20px" height="20px" />
          )}
        </Button>
        <audio
          ref={audioRef}
          src={audioSrc}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
        />
      </div>
    </Card>
  );
};

export default AudioPlayer;
